<template>
  <div class="weapon_wrap">
    <div class="weapon-info container">
      <h1 class="weapon-info__title">
        {{ $t('all_skins', {name: $t('filters.weapon.' + category?.category)}) }}
      </h1>
      <p class="weapon-info__description">
        {{ $t('weapon_page_title', {name: $t('filters.weapon.' + category?.category)}) }}
      </p>
    </div>

    <div class="weapon-categories container">
      <div class="weapon-categories__desktop">
        <NuxtLink
            v-for="cat in category?.items"
            :to="getWeaponLink(cat?.weapon || '')"
            class="btn btn-primary-stroke f fs"
        >
          <img
              :alt="(cat?.full_name || '') + ' category item'"
              :src="getImageFromWaxpeer( cat?.full_name || '', '120:120')"
              class="link-icon"
              height="60"
              loading="lazy"
              width="60"
          />
          <span class="link-name">{{ $t('filters.weapon.' + cat?.weapon) }}</span>
        </NuxtLink>
      </div>

      <UiCollapse
          :items="category?.items"
          class="weapon-categories__mobile w-100"
          item-key="weapon-category">
        <template #label>
          <span class="f gap8">
            <svg class="primary" height="20" width="20">
              <use xlink:href="/icons/sprite.svg#i-category"></use>
            </svg>
            <span class="item-title">
              {{ $t('sub_categories') }}
            </span>
          </span>
        </template>
        <template #items>
          <NuxtLink
              v-for="cat in category?.items"
              :to="getWeaponLink(cat?.weapon || '')"
              class="btn btn-primary-stroke f fs mb8"
          >
            <img
                :alt="(cat?.full_name || '') + ' category item'"
                :src="getImageFromWaxpeer( cat?.full_name || '', '120:120')"
                class="link-icon"
                height="60"
                loading="lazy"
                width="60"
            />
            <span class="link-name">{{ $t('filters.weapon.' + cat?.weapon) }}</span>
          </NuxtLink>
        </template>
      </UiCollapse>
    </div>

    <div v-if="category.items?.length" class="fg weapon-items">
      <UiItemsGrid>
        <LazyPagesWeaponComponentsSkinCard
            v-for="item in items"
            :key="item?.market_hash_name"
            :item="item"
            :link="getItemLinkWithoutExt({
              name: getName(item.market_hash_name),
              slug: item.link
            })"
        ></LazyPagesWeaponComponentsSkinCard>
      </UiItemsGrid>

      <PaginateV2
          key="paginate"
          v-model="pagination.page"
          :pages-length="pagination.pagesLength"
          @on-page-change="emit('on-page-change', $event)"
      />
    </div>
    <LazyUiNoResult v-if="!pending && !items?.length" class="mxa" />

    <div v-if="pending" class="load_period loading-container mt32">
      <div class="f fc">
        <i class="i-loading ml10"></i>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {Category, ItemsByField} from "~/graphql/generated/schema-types";
import {getItemLinkWithoutExt} from "~/utils/name-utils";

const emit = defineEmits(['on-page-change'])
const props = defineProps<{
  category: Category
  items: ItemsByField[]
  pending: boolean
  pagination: {
    page: number
    pagesLength: number
  }
}>()

const getName = (itemName: string) => {
  if (!itemName.includes('Sticker')) {
    return itemName?.replace(/(?:^\s*|\s*\([^)]*\)\s*|\s*$)/g, '')
  }
  return itemName
}
</script>

<style lang="scss" scoped>
@use 'assets/scss/variables.scss' as *;
@use 'assets/scss/mixins.scss' as *;

.weapon {
  &_wrap {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 7rem;

    @media screen and (max-width: 1250px) {
      margin-top: 0;
    }

    @media screen and (max-width: 979px) {
      .weapon-items {
        padding-right: 16px;
        padding-left: 16px;
      }
    }
  }

  &-info {
    width: 100%;
    color: var(--surfaces-on-surface);
    text-align: center;

    &__title {
      font-weight: 600;
      font-size: 2.8rem;
      line-height: 3.8rem;
      margin-bottom: 20px;
    }

    &__description {
      font-size: 1.6rem;
      opacity: 0.8;
      max-width: 68rem;
      margin: 0 auto;
    }
  }

  &-categories {
    &__desktop {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 1rem;
    }

    &__mobile {
      display: none;
    }

    .btn {
      .link-icon {
        object-fit: contain;
        object-position: center;
        background: radial-gradient(50% 50% at 50% 50%, color-mix(in srgb, var(--accent-secondary) 40%, transparent) 0%, transparent 100%);
      }

      .link-name {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }

  @media screen and (max-width: 1075px) {
    &_wrap {
      gap: 4rem;
    }
    &-info__title {
      font-size: 2.4rem;
      line-height: 3.4rem;
    }
    &-info__description {
      font-size: 1.4rem;
      line-height: 3rem;
    }
    &-categories__desktop {
      display: none;
    }
    &-categories__mobile {
      display: block;
    }
    .btn {
      .link-icon {
        width: 40px;
        height: 40px;
      }

      .link-name {
        font-size: 14px;
      }
    }
  }

  @media screen and (max-width: 768px) {
    &-info__title {
      font-size: 2.2rem;
      line-height: 3.2rem;
      margin-bottom: 0;
    }

    &-info__description {
      line-height: 2.8rem;
    }
  }
}
</style>
